import React from 'react';
import { motion } from 'framer-motion';
import { User, Users, Fingerprint, Scan, ChevronRight } from 'lucide-react';
interface GenderSelectionScreenProps {
  onSelect: (gender: 'female' | 'male' | 'other') => void;
  onSkip?: () => void;
}
export function GenderSelectionScreen({
  onSelect,
  onSkip
}: GenderSelectionScreenProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative z-10 w-full max-w-md mx-auto" data-id="element-1278" data-mp="mbbsu5k0tmha">
      <div className="text-center mb-10" data-id="element-1279" data-mp="3e0wottor962">
        <motion.div className="inline-flex items-center justify-center w-20 h-20 border border-[#FF0000]/20 bg-[#FF0000]/5 mb-6 relative overflow-hidden" initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.1
      }} data-id="element-1280" data-mp="3espjwvwessn">
          <div className="absolute inset-0 bg-[#FF0000]/10 blur-xl" data-id="element-1281" data-mp="2nj2byyg9yrur" />
          <Fingerprint size={40} className="text-[#FF0000] relative z-10 opacity-80" strokeWidth={1.5} data-id="element-1282" data-mp="2il98id6cwby6" />

          {/* Scanning Effect */}
          <motion.div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF0000]/20 to-transparent h-[50%]" animate={{
          top: ['-100%', '200%']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 1
        }} data-id="element-1283" data-mp="1pwbpu8y8gd4w" />

          {/* Corner markers */}
          <div className="absolute top-1.5 left-1.5 w-2 h-2 border-t border-l border-[#FF0000] opacity-50" data-id="element-1284" data-mp="3ba7rtdm7tk52" />
          <div className="absolute top-1.5 right-1.5 w-2 h-2 border-t border-r border-[#FF0000] opacity-50" data-id="element-1285" data-mp="27zk6x35czrfj" />
          <div className="absolute bottom-1.5 left-1.5 w-2 h-2 border-b border-l border-[#FF0000] opacity-50" data-id="element-1286" data-mp="2cwsqn865o3iw" />
          <div className="absolute bottom-1.5 right-1.5 w-2 h-2 border-b border-r border-[#FF0000] opacity-50" data-id="element-1287" data-mp="2hy9p6x163dwb" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.4
      }} data-id="element-1288" data-mp="2lwfqhpj8l19u">
          <div className="inline-block px-3 py-1 mb-4 border border-[#FF0000]/20 text-[10px] font-mono text-[#FF0000] tracking-widest uppercase bg-[#FF0000]/5" data-id="element-1289" data-mp="1y7eha02kxm3i">
            Subject Identification
          </div>
          <h2 className="text-3xl font-bold mb-3 leading-tight text-white tracking-tight" data-id="element-1290" data-mp="16hjg965d07z0">
            Calibrate Analysis
          </h2>
        </motion.div>

        <motion.p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto font-mono" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} data-id="element-1291" data-mp="130457ejkt16y">
          Select subject physiology for accurate pattern matching algorithms.
        </motion.p>
      </div>

      <motion.div className="space-y-3" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.4
    }} data-id="element-1292" data-mp="k2krvyq34weq">
        {[{
        id: 'female',
        label: 'Female',
        icon: User
      }, {
        id: 'male',
        label: 'Male',
        icon: User
      }, {
        id: 'other',
        label: 'Other',
        icon: Users
      }].map((option, index) => <motion.button key={option.id} onClick={() => onSelect(option.id as any)} className="group w-full relative overflow-hidden bg-white/5 hover:bg-[#FF0000]/10 border border-white/10 hover:border-[#FF0000]/50 text-gray-300 hover:text-white py-4 px-5 transition-all duration-300" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5 + index * 0.1,
        duration: 0.4
      }} whileHover={{
        scale: 1.02,
        x: 4
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1293" data-mp="1wg12th13qayu" data-mpi={index}>
            {/* Hover scan line */}
            <div className="absolute top-0 left-0 w-1 h-full bg-[#FF0000] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" data-id="element-1294" data-mp="2y17h0ou30o8z" />

            <div className="flex items-center justify-between relative z-10" data-id="element-1295" data-mp="rf9ttimnzuue">
              <div className="flex items-center gap-3" data-id="element-1296" data-mp="mgq73g08yf0z">
                <div className="p-2 bg-white/5 group-hover:bg-[#FF0000]/20 text-gray-400 group-hover:text-[#FF0000] transition-colors" data-id="element-1297" data-mp="114l0kym2wnf4">
                  <option.icon size={18} strokeWidth={2} data-id="element-1298" data-mp="wctlplcac64t" />
                </div>
                <span className="font-bold text-base tracking-wide" data-id="element-1299" data-mp="1a4q95c30druv">
                  {option.label}
                </span>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1300" data-mp="155n1djvn97j7">
                <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase" data-id="element-1301" data-mp="1k132g8un63p3">
                  Select
                </span>
                <Scan size={14} className="text-[#FF0000]" data-id="element-1302" data-mp="3lu0pfo1qm224" />
              </div>
            </div>
          </motion.button>)}
      </motion.div>

      {onSkip && <motion.button onClick={onSkip} className="w-full mt-6 text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.3
    }} data-id="element-1303" data-mp="aegv9bz6ox6t">
          Skip to Dashboard
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1304" data-mp="tbibjsy6ujq2" />
        </motion.button>}

      <div className="mt-8 text-center" data-id="element-1305" data-mp="126y1hyvtoeqd">
        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-1306" data-mp="127zdq6okr79o">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-1307" data-mp="1c4cgdu23kvgk" />
          Secure Encrypted Session • ID:{' '}
          {Math.random().toString(36).substr(2, 9).toUpperCase()}
        </p>
      </div>
    </motion.div>;
}